import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import BlogCard from '../components/BlogCard';
import { supabase } from '../supabaseClient';
import { calculateReadingTime } from '../utils/blog';
import { DEFAULT_BLOG_DESCRIPTION, DEFAULT_OG_IMAGE, getCanonicalUrl } from '../utils/seo';
import { useBranding } from '../context/BrandingContext';

const BlogPostPage = ({ onNavigate }) => {
  const { slug } = useParams();
  const { branding } = useBranding();
  const [post, setPost] = useState(null);
  const [category, setCategory] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [readingTime, setReadingTime] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const go = (path) => {
    if (typeof onNavigate === 'function') {
      onNavigate(path);
    } else {
      window.location.assign(path);
    }
  };

  useEffect(() => {
    if (!slug) return;
    if (typeof window !== 'undefined') {
      setShareUrl(window.location.href);
    } else {
      setShareUrl(getCanonicalUrl(`/blog/${slug}`));
    }

    const fetchPost = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: postError } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, content, cover_image_url, published_at, category_id, is_published')
          .eq('slug', slug)
          .eq('is_published', true)
          .single();

        if (postError || !data) {
          throw postError || new Error('Post no encontrado');
        }

        let categoryData = null;
        if (data.category_id) {
          const { data: catData } = await supabase
            .from('categories')
            .select('id, name, slug')
            .eq('id', data.category_id)
            .single();
          categoryData = catData || null;
        }

        setPost(data);
        setReadingTime(calculateReadingTime(data.content || data.excerpt || ''));
        setCategory(categoryData);

        let relatedQuery = supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, content, cover_image_url, published_at, category_id')
          .eq('is_published', true)
          .neq('id', data.id)
          .order('published_at', { ascending: false, nullsFirst: false })
          .limit(3);

        if (data.category_id) {
          relatedQuery = relatedQuery.eq('category_id', data.category_id);
        }

        const { data: related } = await relatedQuery;

        let relatedWithCategory = related || [];
        const extraCategoryIds = [
          ...new Set(
            relatedWithCategory
              .map((item) => item.category_id)
              .filter(
                (catId) =>
                  catId &&
                  (!categoryData || catId !== categoryData.id)
              )
          ),
        ];

        let extraCategoryMap = new Map();
        if (extraCategoryIds.length) {
          const { data: extraCats } = await supabase
            .from('categories')
            .select('id, name, slug')
            .in('id', extraCategoryIds);
          extraCategoryMap = new Map(
            (extraCats || []).map((cat) => [cat.id, cat])
          );
        }

        relatedWithCategory = relatedWithCategory.map((item) => ({
          ...item,
          category:
            item.category_id === categoryData?.id
              ? categoryData
              : extraCategoryMap.get(item.category_id) || null,
          readingTime: calculateReadingTime(item.content || item.excerpt || ''),
        }));

        setRelatedPosts(relatedWithCategory);
      } catch (err) {
        console.error('Error cargando post:', err);
        setError('No encontramos esta entrada, quizás fue movida o ya no está disponible.');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const slugPath = slug ? `/blog/${slug}` : '/blog';
  const fallbackShareUrl = shareUrl || getCanonicalUrl(slugPath);
  const shareDescription =
    (post?.excerpt && post.excerpt.trim()) || DEFAULT_BLOG_DESCRIPTION;
  const pageTitle = post ? `${post.title} | Blog El Ancla CR` : 'Blog | El Ancla CR';
  const fallbackOgImage = branding.meta.ogImage || DEFAULT_OG_IMAGE;
  const ogImage = post?.cover_image_url || fallbackOgImage;
  const helmetMarkup = (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={shareDescription} />
      <link rel="canonical" href={fallbackShareUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={shareDescription} />
      <meta property="og:url" content={fallbackShareUrl} />
      <meta property="og:image" content={ogImage} />
      {post?.cover_image_url && (
        <meta property="og:image:secure_url" content={post.cover_image_url} />
      )}
      {post?.published_at && (
        <meta property="article:published_time" content={post.published_at} />
      )}
      {post?.updated_at && (
        <meta property="article:modified_time" content={post.updated_at} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={shareDescription} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );

  const handleNativeShare = async () => {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function' || !post) {
      return;
    }

    try {
      await navigator.share({
        title: post.title,
        text: shareDescription,
        url: fallbackShareUrl,
      });
    } catch (err) {
      console.error('No se pudo compartir:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      const canUseClipboard =
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function';

      if (canUseClipboard) {
        await navigator.clipboard.writeText(fallbackShareUrl);
      } else if (typeof document !== 'undefined') {
        const tempInput = document.createElement('textarea');
        tempInput.value = fallbackShareUrl;
        tempInput.setAttribute('readonly', '');
        tempInput.style.position = 'absolute';
        tempInput.style.left = '-9999px';
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    } catch (err) {
      console.error('No se pudo copiar el enlace:', err);
    }
  };

  const shareTargets = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      href: `https://wa.me/?text=${encodeURIComponent(`${post?.title || 'Blog'} - ${fallbackShareUrl}`)}`,
    },
    {
      id: 'facebook',
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fallbackShareUrl)}`,
    },
    {
      id: 'x',
      label: 'X / Twitter',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(fallbackShareUrl)}&text=${encodeURIComponent(post?.title || 'Descubre este artículo')}`,
    },
  ];
  const canUseNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const renderContent = () => {
    if (!post?.content) return null;
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(post.content);
    if (hasHtml) {
      return (
        <div
          className="prose prose-lg prose-slate max-w-none"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      );
    }
    return post.content.split('\n').map((paragraph, index) => (
      <p key={index} className="text-lg text-gray-700 leading-relaxed mb-6">
        {paragraph}
      </p>
    ));
  };

  if (loading) {
    return (
      <>
        {helmetMarkup}
        <div className="min-h-[60vh] flex items-center justify-center bg-white">
          <div className="space-y-4 text-center">
            <div className="h-2 w-24 bg-gray-200 mx-auto rounded-full animate-pulse" />
            <div className="h-6 w-48 bg-gray-200 mx-auto rounded-full animate-pulse" />
          </div>
        </div>
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        {helmetMarkup}
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Ups, esta entrada no está disponible
          </h1>
          <p className="text-gray-600 mb-6 max-w-lg">
            {error || 'Intenta volver más tarde o explorar otras publicaciones del blog.'}
          </p>
          <button
            type="button"
            onClick={() => go('/blog')}
            className="inline-flex items-center rounded-full bg-brand px-5 py-2 text-white font-semibold shadow-lg"
          >
            Volver al blog
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {helmetMarkup}
      <article className="bg-white">
      <section className="bg-gradient-to-br from-[#EEF2FF] via-white to-[#FDF2F8] py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <button
            type="button"
            onClick={() => go('/blog')}
            className="mb-6 inline-flex items-center text-sm font-semibold text-brand hover:underline"
          >
            ← Volver al blog
          </button>
          {category && (
            <span className="inline-flex items-center rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-gray-700 border border-gray-200 mb-4">
              {category.name}
            </span>
          )}
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 max-w-4xl leading-tight mb-6">
            {post.title}
          </h1>
          {readingTime ? (
            <div className="flex items-center text-gray-500 text-base font-medium">
              <span>{`${readingTime} min de lectura`}</span>
            </div>
          ) : null}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {canUseNativeShare && (
              <button
                type="button"
                onClick={handleNativeShare}
                className="inline-flex items-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand/90 transition-colors"
              >
                Compartir
              </button>
            )}
            {shareTargets.map((target) => (
              <a
                key={target.id}
                href={target.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-brand hover:text-brand transition-colors"
              >
                {target.label}
              </a>
            ))}
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-brand hover:text-brand transition-colors"
            >
              Copiar enlace
            </button>
          </div>
          {copySuccess && (
            <p className="text-sm text-green-600 mt-2">Enlace copiado al portapapeles.</p>
          )}
        </div>
      </section>

      {post.cover_image_url && (
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="rounded-[32px] overflow-hidden shadow-2xl -mt-16 mb-10 border border-white">
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="w-full max-h-[520px] object-cover"
            />
          </div>
        </div>
      )}

      <section className="pb-16 sm:pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-4xl">
          <div className="text-lg leading-relaxed text-gray-800 space-y-6">
            {renderContent()}
          </div>
        </div>
      </section>

      {relatedPosts.length > 0 && (
        <section className="bg-[#F9FAFB] py-16 sm:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-10">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-sm uppercase tracking-[0.4em] text-brand font-semibold mb-2">
                  Recomendado
                </p>
                <h2 className="text-3xl font-bold text-gray-900">
                  También podría interesarte
                </h2>
              </div>
              <button
                type="button"
                onClick={() => go('/blog')}
                className="text-sm font-semibold text-brand hover:underline"
              >
                Ver todas las entradas
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((item) => (
                <BlogCard
                  key={item.id}
                  post={item}
                  category={item.category || (item.category_id === category?.id ? category : null)}
                  readingTime={item.readingTime}
                  onClick={() => go(`/blog/${item.slug}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}
      </article>
    </>
  );
};

export default BlogPostPage;
